import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Linkedin, Facebook, Download } from 'lucide-react';
import { useState, useEffect } from 'react';

const footerLinks = {
  services: [
    { name: 'Corporate Recyclable Management', href: '/services/corporate-recyclable-management' },
    { name: 'Factory Waste Management', href: '/services/factory-waste-management' },
    { name: 'Plastic Waste Management', href: '/services/plastic-waste-management' },
    { name: 'EPR Services', href: '/services/epr-services' },
    { name: 'Scrap Trading', href: '/services/scrap-trading' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Our Impact', href: '/impact' },
    { name: 'Insights', href: '/insights' },
    { name: 'Contact', href: '/contact' },
  ],
  states: [
    'Jharkhand',
    'Bihar',
    'West Bengal',
    'Uttar Pradesh',
    'Madhya Pradesh',
    'Chhattisgarh',
    'Maharashtra',
  ],
};

// Use a fixed year for SSR to prevent hydration mismatch
const CURRENT_YEAR = 2026;

export function Footer() {
  // Use state to handle year on client to avoid hydration mismatch
  const [year, setYear] = useState(CURRENT_YEAR);

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);
  return (
    <footer className="bg-eco-green-800 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <picture>
                <source srcSet="/images/logo.webp" type="image/webp" />
                <img
                  src="/images/logo.png"
                  alt="Eco Enviro"
                  width={160}
                  height={82}
                  className="h-16 w-auto brightness-0 invert"
                  loading="lazy"
                />
              </picture>
            </Link>
            <p className="text-eco-green-200 text-sm leading-relaxed mb-4">
              <strong className="text-white">Eco Enviro Circular Solutions</strong>
              <br />
              Argora, Ranchi, Jharkhand, India
            </p>
            <div className="flex items-center gap-3 mb-4">
              <a
                href="https://www.linkedin.com/company/theecochakra"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-eco-green-700 rounded-lg flex items-center justify-center hover:bg-eco-green-600 hover:scale-110 hover:-translate-y-0.5 transition-all"
                title="Follow us on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/theecochakra"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-eco-green-700 rounded-lg flex items-center justify-center hover:bg-eco-green-600 hover:scale-110 hover:-translate-y-0.5 transition-all"
                title="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
            {/* Download Company Profile */}
            <a
              href="/pdfs/Eco_Enviro_Draft.pdf"
              download
              className="inline-flex items-center gap-2 px-4 py-2 bg-eco-green-700 rounded-lg text-sm text-white hover:bg-eco-green-600 hover:scale-[1.02] transition-all"
            >
              <Download className="w-4 h-4" />
              Download Company Profile
            </a>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-base font-semibold mb-5 text-white">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-eco-green-200 hover:text-white transition-colors text-sm inline-block hover:translate-x-1 duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & States */}
          <div>
            <h4 className="text-base font-semibold mb-5 text-white">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-eco-green-200 hover:text-white transition-colors text-sm inline-block hover:translate-x-1 duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-base font-semibold mt-8 mb-4 text-white">States Served</h4>
            <p className="text-eco-green-300 text-xs leading-relaxed">
              {footerLinks.states.join(' • ')}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-base font-semibold mb-5 text-white">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-eco-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-eco-green-200 text-sm">
                  <p className="font-medium text-white mb-1">Head Office</p>
                  <p>
                    2126-A1, Vashundra Apartment, Argora By-Pass, Argora, Ranchi - 834002, Jharkhand
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-eco-green-400 flex-shrink-0" />
                <a
                  href="mailto:info@theecoenviro.com"
                  className="text-eco-green-200 hover:text-white transition-colors text-sm"
                >
                  info@theecoenviro.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-eco-green-400 flex-shrink-0" />
                <a
                  href="tel:+918619433916"
                  className="text-eco-green-200 hover:text-white transition-colors text-sm"
                >
                  +91 86194 33916
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-eco-green-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <p className="text-eco-green-300 text-sm">
              © {year} Eco Enviro Circular Solutions. All rights reserved.
            </p>
            <a
              href="https://theecoenviro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-eco-green-400 text-sm hover:text-white transition-colors"
            >
              www.theecoenviro.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
